import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { DEVICE_STORAGE_KEYS } from "./accountStorage";

import authEn from "./locales/en/auth.json";
import chatEn from "./locales/en/chat.json";
import commonEn from "./locales/en/common.json";
import desktopEn from "./locales/en/desktop.json";
import environmentsEn from "./locales/en/environments.json";
import routesEn from "./locales/en/routes.json";
import settingsEn from "./locales/en/settings.json";
import sidebarEn from "./locales/en/sidebar.json";
import authRu from "./locales/ru/auth.json";
import chatRu from "./locales/ru/chat.json";
import commonRu from "./locales/ru/common.json";
import desktopRu from "./locales/ru/desktop.json";
import environmentsRu from "./locales/ru/environments.json";
import routesRu from "./locales/ru/routes.json";
import settingsRu from "./locales/ru/settings.json";
import sidebarRu from "./locales/ru/sidebar.json";

export const LOCALES = ["en", "ru"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "ru";

/** Source locale. Explicit, so a missed key renders readable text. */
export const FALLBACK_LOCALE: Locale = "en";

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  ru: "Русский",
};

export const NAMESPACES = [
  "auth",
  "chat",
  "common",
  "desktop",
  "environments",
  "routes",
  "settings",
  "sidebar",
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/**
 * A minted message key, always `<namespace>/<slug>_<hash6>`. Keys are generated
 * from the catalogue by `tools/mint-i18n-keys.mjs` and never written by hand,
 * so this stays a plain string rather than a union of 1,197 literals.
 */
export type I18nKey = string;

const resources = {
  en: {
    auth: authEn,
    chat: chatEn,
    common: commonEn,
    desktop: desktopEn,
    environments: environmentsEn,
    routes: routesEn,
    settings: settingsEn,
    sidebar: sidebarEn,
  },
  ru: {
    auth: authRu,
    chat: chatRu,
    common: commonRu,
    desktop: desktopRu,
    environments: environmentsRu,
    routes: routesRu,
    settings: settingsRu,
    sidebar: sidebarRu,
  },
} as const;

const STORAGE_KEY = DEVICE_STORAGE_KEYS.locale;

function hasLocaleStorage() {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export function isLocale(value: unknown): value is Locale {
  return LOCALES.includes(value as Locale);
}

export function getStoredLocale(): Locale {
  if (!hasLocaleStorage()) return DEFAULT_LOCALE;
  const raw = localStorage.getItem(STORAGE_KEY);
  return isLocale(raw) ? raw : DEFAULT_LOCALE;
}

/**
 * `initReactI18next` registers this instance as react-i18next's default, so
 * `useTranslation()` resolves without a surrounding provider. That matters
 * beyond convenience: many components sit under no provider, and without this
 * every such component warns `NO_I18NEXT_INSTANCE` and renders its key.
 * The explicit `I18nextProvider` at the router stays — it is what re-renders the
 * tree on a locale change.
 */
void i18next.use(initReactI18next).init({
  resources,
  // Russian without the user doing anything. `getStoredLocale` falls
  // back to it, so an unset preference is Russian, not the source locale.
  lng: getStoredLocale(),
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: [...LOCALES],
  ns: [...NAMESPACES],
  defaultNS: "common",
  /**
   * Keys carry a literal `.` in ordinary prose ("Delete this thread."), and a
   * key separator would reinterpret it as a path into a nested object and miss
   * silently. Disabling it is what makes flat, hashed keys safe.
   */
  keySeparator: false,
  /**
   * `<namespace>/<key>`. Not the default `:` — a colon appears inside message
   * text far more often than a slash appears inside a minted key.
   */
  nsSeparator: "/",
  interpolation: {
    // React escapes on render; escaping here too double-encodes every
    // apostrophe and angle bracket that reaches the DOM.
    escapeValue: false,
  },
  returnEmptyString: false,
  // Off by default upstream; naming it here documents that a miss is silent and
  // that the coverage gate, not the runtime, is what catches one.
  saveMissing: false,
});

function applyDocumentLanguage(locale: Locale) {
  // `document` can exist without a `documentElement`: some suites install a
  // partial DOM stub, and this module is reachable from much of the graph.
  if (typeof document === "undefined") return;
  const root: HTMLElement | null | undefined = document.documentElement;
  if (!root) return;
  root.lang = locale;
}

applyDocumentLanguage(getStoredLocale());

export function setLocale(locale: Locale) {
  if (hasLocaleStorage()) {
    localStorage.setItem(STORAGE_KEY, locale);
  }
  void i18next.changeLanguage(locale);
  applyDocumentLanguage(locale);
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      const locale = getStoredLocale();
      void i18next.changeLanguage(locale);
      applyDocumentLanguage(locale);
    }
  });
}

/**
 * Module-scope translator, for the many call sites that are plain functions
 * rather than components: a call against the singleton instance. Components that
 * must re-render on a locale change use `useTranslation()` instead.
 */
export const t = i18next.t.bind(i18next);

export const i18n = i18next;
export default i18next;
